import { For, Show } from '@agro1desenvolvimento/agro1-design-system/components/atoms';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@agro1desenvolvimento/agro1-design-system/components/ui';
import { Link, useRouterState } from '@tanstack/react-router';
import { cva } from 'class-variance-authority';
import { ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useUiStore } from '@/stores/ui-store';

import { CollapseMenuIcon, ExpandMenuIcon, HomeIcon, SeedLabIcon } from './icons';

const railItemVariants = cva(
  'flex h-14 w-full flex-none items-center justify-center border-l-[3px] disabled:cursor-not-allowed',
  {
    variants: {
      active: {
        true: 'border-l-leaf bg-wash text-moss',
        false: 'border-l-transparent text-hint hover:bg-panel',
      },
    },
    defaultVariants: { active: false },
  },
);

const menuItemVariants = cva(
  'flex h-14 w-full flex-none items-center gap-2 border-l-[3px] px-4 text-left text-sm disabled:cursor-not-allowed',
  {
    variants: {
      active: {
        true: 'border-l-leaf bg-wash font-medium text-moss',
        false: 'border-l-transparent font-normal text-hint hover:bg-panel',
      },
    },
    defaultVariants: { active: false },
  },
);

const subItemVariants = cva('flex h-14 flex-none items-center pl-10 pr-4 text-sm', {
  variants: {
    active: {
      true: 'bg-wash font-semibold text-moss',
      false: 'font-normal text-hint hover:bg-panel hover:text-moss',
    },
  },
  defaultVariants: { active: false },
});

const ICON_SLOT_CLASSES = 'mr-close grid size-4.5 flex-none place-content-center';

type LabTab = 'tetrazolio' | 'canteiro-areia' | 'germinacao' | 'envelhecimento';

type LabItem = { key: string; labelKey: string; tab?: LabTab };

const LAB_ITEMS: LabItem[] = [
  { key: 'tetrazolio', labelKey: 'navigation.tetrazolium', tab: 'tetrazolio' },
  { key: 'canteiro-areia', labelKey: 'navigation.sandBed', tab: 'canteiro-areia' },
  { key: 'germinacao', labelKey: 'navigation.germination', tab: 'germinacao' },
  { key: 'envelhecimento', labelKey: 'navigation.acceleratedAging', tab: 'envelhecimento' },
];

const LAB_ROUTE_PREFIX = '/seeds';

function useActiveTab(): string {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const search = useRouterState({ select: (state) => state.location.search }) as { tab?: string };

  if (pathname.includes('/sand-bed')) return 'canteiro-areia';
  if (pathname.includes('/tetrazolium')) return 'tetrazolio';
  if (pathname.includes('/germination')) return 'germinacao';
  if (pathname.includes('/accelerated-aging')) return 'envelhecimento';

  return search.tab ?? 'tetrazolio';
}

function useIsLabActive(): boolean {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return pathname.startsWith(LAB_ROUTE_PREFIX);
}

export function AppSidebar() {
  const { t } = useTranslation();
  const sidebarOpen = useUiStore((state) => state.sidebarOpen);
  const setSidebarOpen = useUiStore((state) => state.setSidebarOpen);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);
  const activeTab = useActiveTab();
  const isLabActive = useIsLabActive();

  return (
    <>
      <aside className="border-canvas flex w-14 flex-none flex-col border-r bg-white">
        <button
          aria-label={t('navigation.expandMenu')}
          className="border-wash text-hint hover:bg-panel flex h-18 w-full flex-none items-center justify-center border-b"
          type="button"
          onClick={toggleSidebar}
        >
          <ExpandMenuIcon />
        </button>

        <span className="h-4 flex-none" />

        <button
          aria-label={t('navigation.home')}
          className={railItemVariants()}
          disabled
          type="button"
        >
          <HomeIcon />
        </button>

        <button
          aria-label={t('navigation.laboratory')}
          className={railItemVariants({ active: isLabActive })}
          type="button"
          onClick={toggleSidebar}
        >
          <SeedLabIcon />
        </button>
      </aside>

      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent
          className="w-70 gap-0 border-r-0 bg-white p-0 shadow-none data-[state=closed]:duration-150 data-[state=open]:duration-200 sm:max-w-none [&>button]:hidden"
          side="left"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{t('navigation.menu')}</SheetTitle>
            <SheetDescription>{t('navigation.menuDescription')}</SheetDescription>
          </SheetHeader>

          <div className="shadow-card flex h-full w-full flex-col overflow-y-auto bg-white">
            <div className="border-wash flex h-18 flex-none items-center gap-4 border-b px-4">
              <img alt={t('app.name')} className="h-6 w-auto" src="/assets/agro1-logo.svg" />
              <span className="flex-1" />
              <button
                aria-label={t('navigation.collapseMenu')}
                className="text-hint hover:bg-panel grid size-8 flex-none place-content-center rounded-lg"
                type="button"
                onClick={toggleSidebar}
              >
                <CollapseMenuIcon />
              </button>
            </div>

            <span className="h-4 flex-none" />

            <button className={menuItemVariants()} disabled type="button">
              <span className={ICON_SLOT_CLASSES}>
                <HomeIcon />
              </span>
              {t('navigation.home')}
            </button>

            <Collapsible defaultOpen className="group/lab flex flex-col">
              <CollapsibleTrigger className={menuItemVariants({ active: isLabActive })}>
                <span className={ICON_SLOT_CLASSES}>
                  <SeedLabIcon />
                </span>
                <span className="flex-1">{t('navigation.laboratory')}</span>
                <ChevronUp className="size-4.5 flex-none transition-transform group-data-[state=closed]/lab:rotate-180" />
              </CollapsibleTrigger>

              <CollapsibleContent className="flex flex-col pb-2">
                <For each={LAB_ITEMS}>
                  {({ key, tab, labelKey }) => (
                    <Show
                      key={key}
                      fallback={<span className={subItemVariants()}>{t(labelKey)}</span>}
                      when={tab}
                    >
                      <Link
                        className={subItemVariants({ active: key === activeTab })}
                        search={{ tab: tab as LabTab }}
                        to={LAB_ROUTE_PREFIX}
                        onClick={() => setSidebarOpen(false)}
                      >
                        {t(labelKey)}
                      </Link>
                    </Show>
                  )}
                </For>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
